import { onlineManager } from '@tanstack/react-query';
import { SyncState } from '@doorly/shared';
import { db, type MutationRow } from '../../db/schema';
import { apiClient, type ApiError } from '../../lib/apiClient';

type TokenProvider = () => Promise<string | null>;

const LOCK_NAME = 'doorly-sync';
const MAX_ATTEMPTS = 10;
const BACKOFF_BASE_MS = 5_000;
const BACKOFF_MAX_MS = 10 * 60_000;

/**
 * Singleton that drains `db.mutations` FIFO whenever the app is online.
 *
 * - Only one tab holds `navigator.locks.request('doorly-sync')` at a time, so no double-send.
 * - Each mutation carries its own `Idempotency-Key` in the DB; if the server already processed
 *   it (crashed client, Background Sync replay), the stored response is replayed with 200.
 * - PATCH /leads/:id returns 409 when the server's `version` moved — we tag the lead with
 *   `_syncState: 'conflict'` and stop retrying; the UI surfaces a resolution prompt.
 */
class SyncEngineImpl {
  private getToken: TokenProvider | null = null;
  private running = false;
  private unsubOnline: (() => void) | null = null;

  configure(getToken: TokenProvider) {
    this.getToken = getToken;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.unsubOnline = onlineManager.subscribe((online) => {
      if (online) void this.tick();
    });
    // Une mutation `in_flight` au démarrage a été interrompue (rechargement,
    // fermeture d'onglet) : rien d'autre ne la reprendrait. Sans risque de
    // doublon : le rejeu porte le même `Idempotency-Key`.
    void db.mutations
      .where('status')
      .equals('in_flight')
      .modify({ status: 'queued' })
      .then(() => this.tick());
  }

  stop() {
    this.running = false;
    this.unsubOnline?.();
    this.unsubOnline = null;
  }

  /**
   * User-initiated retry: resets backoff on queued/failed/dead items and kicks
   * the loop. Dead mutations are typically 4xx rejections — a human has fixed
   * the underlying issue (e.g. missing field on user, server-side config) and
   * wants to retry the parked payload.
   */
  async forceFlush(): Promise<void> {
    const now = new Date().toISOString();
    await db.transaction('rw', [db.mutations, db.leads], async () => {
      const toRetry = await db.mutations
        .where('status')
        .anyOf('queued', 'failed', 'dead')
        .toArray();
      const entityIds = toRetry.map((m) => m.entityId);
      await db.mutations
        .where('id')
        .anyOf(toRetry.map((m) => m.id))
        .modify({ nextAttemptAt: now, attempts: 0, lastError: undefined, status: 'queued' });
      // Unblock any lead that was parked in failed/conflict state.
      await db.leads
        .where('id')
        .anyOf(entityIds)
        .modify({ _syncState: SyncState.pending, _lastError: undefined });
    });
    await this.tick();
  }

  private async tick(): Promise<void> {
    if (!this.running || !this.getToken) return;
    if (!onlineManager.isOnline()) return;
    if (!('locks' in navigator)) return void this.drain();
    await navigator.locks.request(LOCK_NAME, { ifAvailable: true }, async (lock) => {
      if (!lock) return; // another tab is draining
      await this.drain();
    });
  }

  private async drain(): Promise<void> {
    while (this.running && onlineManager.isOnline()) {
      const mutation = await this.pickNext();
      if (!mutation) return;
      await this.process(mutation);
    }
  }

  private async pickNext(): Promise<MutationRow | null> {
    const now = Date.now();
    const candidate = await db.mutations
      .where('status')
      .equals('queued')
      .filter((m) => Date.parse(m.nextAttemptAt) <= now)
      .first();
    if (!candidate) return null;
    await db.mutations.update(candidate.id, { status: 'in_flight' });
    return { ...candidate, status: 'in_flight' };
  }

  private async process(mutation: MutationRow): Promise<void> {
    const token = await this.getToken!();
    if (mutation.entity === 'pin_switch') {
      await this.processPinSwitch(mutation, token);
      return;
    }
    // Chaque fiche part sous l'identité de SON auteur, pas du vendeur actif
    // au moment du sync (F6). `null` = niveau tablette : la session elle-même
    // a produit la mutation, sans bascule PIN. Sans cette précision, `apiClient`
    // reprendrait le vendeur actif et l'API refuserait la fiche comme
    // usurpation (spec §6.1).
    const actingAs = mutation.actorUserId ?? null;
    try {
      // Parse the response so we can sync the server-side version back to
      // Dexie — otherwise a subsequent PATCH would use version=0 locally
      // against a server that's already at 1 (409 conflict).
      type LeadResponse = {
        version?: number;
        /** Mode différé hors-ligne : le serveur a envoyé le SMS au sync,
         *  on récupère le smsLogId + sa deadline pour afficher le countdown
         *  côté front. */
        consentStatus?: 'verified' | 'pending_offline' | 'expired';
        consentExpiresAt?: string | null;
        consentSmsLogId?: string | null;
        /** Magasin retenu par le serveur (il revérifie celui de la tablette). */
        storeId?: string | null;
        store?: { id: string; name: string } | null;
        /** Mis en forme « Prénom Nom » par le serveur. */
        firstName?: string;
        lastName?: string;
      };
      let response: LeadResponse | undefined;
      const send = (as: string | null) =>
        mutation.op === 'create'
          ? apiClient.post<LeadResponse>(`/leads`, mutation.payload, {
              token,
              idempotencyKey: mutation.id,
              actingAs: as,
            })
          : apiClient.patch<LeadResponse>(`/leads/${mutation.entityId}`, mutation.payload, {
              token,
              idempotencyKey: mutation.id,
              actingAs: as,
            });
      try {
        response = await send(actingAs);
      } catch (err) {
        // L'acteur n'est plus recevable (PIN désactivé sur le réseau, vendeur
        // archivé ou rattaché ailleurs entre la saisie et le sync) : la fiche
        // repart au niveau tablette. Le payload porte toujours le
        // `commercialId` de l'auteur et la session du superviseur a le droit
        // de créer pour ses vendeurs — la fiche n'est pas perdue, et reste
        // attribuée à qui l'a saisie.
        if (actingAs && (err as Partial<ApiError>).status === 403) {
          response = await send(null);
        } else {
          throw err;
        }
      }
      await db.transaction('rw', db.leads, db.mutations, async () => {
        const patch: Partial<{
          _syncState: SyncState;
          _localOnly: boolean;
          _lastError: undefined;
          version: number;
          consentStatus: 'verified' | 'pending_offline' | 'expired';
          consentExpiresAt: string | null;
          consentSmsLogId: string | null;
          storeId: string | null;
          store: { id: string; name: string } | null;
          firstName: string;
          lastName: string;
        }> = {
          _syncState: SyncState.synced,
          _localOnly: false,
          _lastError: undefined,
        };
        if (typeof response?.version === 'number') {
          patch.version = response.version;
        }
        if (response?.consentStatus) {
          patch.consentStatus = response.consentStatus;
        }
        if (response && 'consentExpiresAt' in response) {
          patch.consentExpiresAt = response.consentExpiresAt ?? null;
        }
        if (response && 'consentSmsLogId' in response) {
          patch.consentSmsLogId = response.consentSmsLogId ?? null;
        }
        if (mutation.op === 'create' && response && 'storeId' in response) {
          patch.storeId = response.storeId ?? null;
          patch.store = response.store ?? null;
        }
        // Casse « Prénom Nom » appliquée par le serveur : la fiche locale
        // reprend la forme enregistrée (création comme modification).
        if (response?.firstName) patch.firstName = response.firstName;
        if (response?.lastName) patch.lastName = response.lastName;
        await db.leads.update(mutation.entityId, patch);
        await db.mutations.delete(mutation.id);
      });
    } catch (err) {
      await this.onError(mutation, err);
    }
  }

  /**
   * Déclaration d'une bascule PIN vérifiée hors ligne (`verifiedBy: device`).
   * Part au niveau tablette (`actingAs: null`) : c'est la session du
   * superviseur qui déclare, et l'idempotence évite le doublon au rejeu.
   * Aucune fiche n'en dépend : un refus 4xx est simplement journalisé et la
   * mutation abandonnée — la trace serveur est perdue, pas le travail.
   */
  private async processPinSwitch(mutation: MutationRow, token: string | null): Promise<void> {
    try {
      await apiClient.post('/pin/switch-logs', mutation.payload, {
        token,
        idempotencyKey: mutation.id,
        actingAs: null,
      });
      await db.mutations.delete(mutation.id);
    } catch (err) {
      const status = (err as Partial<ApiError>).status ?? 0;
      const message = err instanceof Error ? err.message : String(err);
      if (status >= 400 && status < 500) {
        console.warn('[sync] déclaration de bascule PIN refusée, abandonnée :', message);
        await db.mutations.delete(mutation.id);
        return;
      }
      if (mutation.attempts + 1 > MAX_ATTEMPTS) {
        await db.mutations.delete(mutation.id);
        return;
      }
      await this.scheduleRetry(mutation, message);
    }
  }

  /** Réseau / 5xx : rejoue plus tard, backoff exponentiel avec un peu de bruit. */
  private async scheduleRetry(mutation: MutationRow, message: string): Promise<void> {
    const attempts = mutation.attempts + 1;
    const delay = Math.min(
      BACKOFF_BASE_MS * 2 ** (attempts - 1) + Math.random() * 1_000,
      BACKOFF_MAX_MS,
    );
    await db.mutations.update(mutation.id, {
      attempts,
      status: 'queued',
      nextAttemptAt: new Date(Date.now() + delay).toISOString(),
      lastError: message,
    });
  }

  private async onError(mutation: MutationRow, err: unknown): Promise<void> {
    const apiErr = err as Partial<ApiError>;
    const status = apiErr.status ?? 0;
    const message = err instanceof Error ? err.message : String(err);

    // 409 → version conflict. Park the lead in `conflict` state; operator must intervene.
    if (status === 409) {
      await db.transaction('rw', db.leads, db.mutations, async () => {
        await db.leads.update(mutation.entityId, {
          _syncState: SyncState.conflict,
          _lastError: message,
        });
        await db.mutations.update(mutation.id, { status: 'dead', lastError: message });
      });
      return;
    }

    // 4xx other than 409 → bad request, no point retrying.
    if (status >= 400 && status < 500) {
      await db.transaction('rw', db.leads, db.mutations, async () => {
        await db.leads.update(mutation.entityId, {
          _syncState: SyncState.failed,
          _lastError: message,
        });
        await db.mutations.update(mutation.id, { status: 'dead', lastError: message });
      });
      return;
    }

    // Network / 5xx → backoff retry.
    const attempts = mutation.attempts + 1;
    if (attempts > MAX_ATTEMPTS) {
      await db.transaction('rw', db.leads, db.mutations, async () => {
        await db.leads.update(mutation.entityId, {
          _syncState: SyncState.failed,
          _lastError: message,
        });
        await db.mutations.update(mutation.id, { status: 'dead', lastError: message });
      });
      return;
    }
    await this.scheduleRetry(mutation, message);
  }
}

export const SyncEngine = new SyncEngineImpl();
